import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Heart, Lock, Mail, Eye, EyeOff } from 'lucide-react';

const ActivatePage = () => {
  const [params] = useSearchParams();
  const uid = params.get('uid');
  const suggestedEmail = params.get('email') || '';
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const [pending, setPending] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState(suggestedEmail);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (!uid) { setError('This link is missing an invite ID.'); return; }
    axios.get(`${API}/auth/pending/${uid}`)
      .then(r => {
        setPending(r.data);
        setEmail(prev => prev || r.data.suggested_email || '');
      })
      .catch(e => setError(e.response?.data?.detail || 'This invite is no longer valid.'));
  }, [uid]);

  const handleActivate = async (e) => {
    e.preventDefault();
    if (!email || password.length < 6) { toast.error('Please enter your email and a password of at least 6 characters.'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/activate-pending`, { uid, email, password });
      toast.success(`Welcome, ${res.data.user.name}!`);
      login(res.data.user, true);
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not activate your account.');
    }
    setLoading(false);
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-5" data-testid="activate-error">
        <div className="max-w-sm w-full text-center surface-card p-8">
          <h2 className="text-xl font-bold text-foreground">Invite unavailable</h2>
          <p className="text-sm text-muted-foreground mt-2">{error}</p>
          <Button className="mt-6 w-full" onClick={() => navigate('/auth')}>Sign in instead</Button>
        </div>
      </div>
    );
  }

  if (!pending) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground text-sm">Loading your invite…</div>;
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-5" data-testid="activate-page">
      <div className="max-w-sm w-full fade-in">
        <div className="surface-card p-7 shadow-lg">
          <div className="text-center mb-6">
            <div className="relative w-14 h-14 flex items-center justify-center mx-auto mb-4">
              <div className="absolute inset-0 rounded-full border-2 border-cyan-300/40" />
              <div className="absolute inset-2 rounded-full border-2 border-cyan-400/60" />
              <div className="w-7 h-7 bg-red-500 rounded-full flex items-center justify-center">
                <Heart className="w-3.5 h-3.5 text-white" fill="currentColor" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-foreground tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Hi {pending.recipient_first_name},
            </h2>
            <p className="text-sm text-muted-foreground mt-2">
              {pending.coordinator_name || 'Someone'} set up <strong className="text-foreground">{pending.circle_name || 'a care circle'}</strong> for you.
              Set a password to see it.
            </p>
          </div>

          <form onSubmit={handleActivate} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="a-email" className="text-sm font-medium">Your email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input id="a-email" type="email" data-testid="activate-email-input" value={email} onChange={e => setEmail(e.target.value)} required className="pl-10 rounded-xl" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="a-password" className="text-sm font-medium">Choose a password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input id="a-password" type={showPassword ? 'text' : 'password'} data-testid="activate-password-input" value={password} onChange={e => setPassword(e.target.value)} required className="pl-10 pr-10 rounded-xl" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" className="w-full btn-primary" disabled={loading} data-testid="activate-submit-btn">
              {loading ? 'Setting up your account…' : 'Join my circle →'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ActivatePage;
